/**
 * Ranked name search — shared vocabulary for every search box that orders by match quality.
 *
 * The ranking itself lives in the database (`public.search_match_tier`, migration
 * 2026-09-17_ranked_name_search.sql): exact > starts with > a word starts with > contains.
 * These helpers keep the frontend's plain filter queries (header counts, non-ranked sorts)
 * matching exactly the same rows as the ranked functions, so a count never disagrees with the list.
 */

import type { SortOption } from "@/components/shared/SortMenu";

/** Menu label for the ranked sort. */
export const RELEVANCE_SORT_LABEL = "Best match";

/**
 * A search term safe to embed in a PostgREST `.or(...)` filter string: trimmed, with the
 * characters that filter grammar reserves (`,` `(` `)`) removed. Pass the SAME value to the
 * ranked function so the grid and the counts search for the same thing.
 */
export function toOrFilterSearchTerm(raw: string): string {
  return raw.replace(/[,()]/g, "").trim();
}

/**
 * The ILIKE form of a user term — the frontend mirror of `public.search_like_pattern`:
 * `\`, `%` and `_` are escaped so they match literally (without it "100%" also matches "1000"),
 * and `*` becomes `_`, matching exactly one character. PostgREST rewrites any `*` left in a
 * like/ilike value into `%` and has no escape for it, so a literal `*` cannot be expressed here;
 * both sides agree on "one character" instead, which still finds censored titles like "F**K".
 */
export function escapeIlikeTerm(term: string): string {
  return term.replace(/[\\%_]/g, (ch) => `\\${ch}`).replace(/\*/g, "_");
}

/** `%term%` with the term escaped — for `.ilike(column, …)` and `column.ilike.…` filters. */
export function ilikeContainsPattern(term: string): string {
  return `%${escapeIlikeTerm(term)}%`;
}

/**
 * The sort options a page's menu should offer: "Best match" first while a search is active,
 * the page's own options otherwise (ranking has nothing to rank without a search).
 */
export function withRelevanceOption<T extends string>(
  options: readonly SortOption<T>[],
  relevanceValue: T,
  searchActive: boolean
): readonly SortOption<T>[] {
  if (!searchActive) return options;
  return [{ value: relevanceValue, label: RELEVANCE_SORT_LABEL }, ...options];
}

/**
 * Whether results should be ranked: a search is active and the user has not picked a different
 * sort since this search started. Owner decision 2026-09-17 — "Best match" is selected
 * automatically when a search starts; choosing another sort gives that sort plainly; the next
 * search starts on "Best match" again.
 */
export function isRankedSearch(searchActive: boolean, sortPickedDuringSearch: boolean): boolean {
  return searchActive && !sortPickedDuringSearch;
}
